import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Mail, Linkedin, MapPin, Send } from "lucide-react";
import { CONTACT_INFO, subscribeNewsletter } from "@/lib/api";

const Footer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await subscribeNewsletter(email.trim());
      toast.success(t("footer.newsletter.success"));
      setEmail("");
    } catch (err) {
      toast.error(t("footer.newsletter.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-border bg-forest-700 text-forest-50 dark:bg-[#0A1C17] mt-24"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10">
          {/* Brand & tagline */}
          <div className="lg:col-span-5">
            <Link to="/" className="inline-flex items-center gap-4 mb-6 group">
              <img
                src="/logo.png"
                alt="PT Agrikultural Nusantara Perkasa"
                data-testid="footer-logo-image"
                className="h-16 md:h-20 w-auto object-contain drop-shadow-[0_2px_10px_rgba(0,0,0,0.4)]"
              />
            </Link>
            <p className="font-serif italic text-2xl lg:text-3xl text-forest-100 max-w-md leading-snug">
              {t("footer.tagline")}
            </p>

            <div className="mt-10">
              <p className="text-xs uppercase tracking-[0.22em] text-forest-200 mb-4">
                {t("footer.newsletter.title")}
              </p>
              <p className="text-sm text-forest-100/80 max-w-md mb-4">
                {t("footer.newsletter.subtitle")}
              </p>
              <form onSubmit={submit} className="flex items-center gap-2 border-b border-forest-400/40 focus-within:border-gold-400 transition-colors max-w-md">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="newsletter-email"
                  placeholder={t("footer.newsletter.placeholder")}
                  className="flex-1 bg-transparent py-3 outline-none text-sm text-forest-50 placeholder:text-forest-200/60"
                />
                <button
                  type="submit"
                  data-testid="newsletter-submit"
                  disabled={busy}
                  className="p-2 text-gold-400 hover:text-gold-200 transition-colors disabled:opacity-50"
                  aria-label={t("cta.subscribe")}
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>

          {/* Nav columns */}
          <div className="lg:col-span-2">
            <p className="overline text-gold-300 mb-6">{t("footer.explore")}</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/" className="hover:text-gold-200 transition-colors">{t("nav.home")}</Link></li>
              <li><Link to="/about" className="hover:text-gold-200 transition-colors">{t("nav.about")}</Link></li>
              <li><Link to="/industries" className="hover:text-gold-200 transition-colors">{t("nav.industries")}</Link></li>
              <li><Link to="/sustainability" className="hover:text-gold-200 transition-colors">{t("nav.sustainability")}</Link></li>
              <li><Link to="/blog" className="hover:text-gold-200 transition-colors">{t("nav.blog")}</Link></li>
              <li><Link to="/contact" className="hover:text-gold-200 transition-colors">{t("nav.contact")}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="overline text-gold-300 mb-6">{t("footer.products_col")}</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/products/green-tea" className="hover:text-gold-200 transition-colors">{t("products.categories.green_tea")}</Link></li>
              <li><Link to="/roasted-green-tea" className="hover:text-gold-200 transition-colors">{t("products.categories.roasted_green_tea")}</Link></li>
              <li><Link to="/products/black-tea" className="hover:text-gold-200 transition-colors">{t("products.categories.black_tea")}</Link></li>
              <li><Link to="/products/oolong-tea" className="hover:text-gold-200 transition-colors">{t("products.categories.oolong_tea")}</Link></li>
              <li><Link to="/products/specialty-tea" className="hover:text-gold-200 transition-colors">{t("products.categories.specialty_tea")}</Link></li>
              <li><Link to="/oem" className="hover:text-gold-200 transition-colors">{t("nav.oem")}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="overline text-gold-300 mb-6">{t("footer.connect")}</p>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={CONTACT_INFO.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="inline-flex items-center gap-2 hover:text-gold-200 transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp — {CONTACT_INFO.whatsapp}
                </a>
              </li>
              <li>
                <a
                  href={CONTACT_INFO.emailHref}
                  data-testid="footer-email"
                  className="inline-flex items-center gap-2 hover:text-gold-200 transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" /> {CONTACT_INFO.email}
                </a>
              </li>
              <li>
                <a
                  href={CONTACT_INFO.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-linkedin"
                  className="inline-flex items-center gap-2 hover:text-gold-200 transition-colors"
                >
                  <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                </a>
              </li>
              <li>
                <a
                  href={CONTACT_INFO.mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-map"
                  className="inline-flex items-center gap-2 hover:text-gold-200 transition-colors"
                >
                  <MapPin className="w-3.5 h-3.5" /> West Java, Indonesia
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-forest-500/30 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-forest-200/70">
          <p>© {new Date().getFullYear()} PT Agrikultural Nusantara Perkasa. {t("footer.rights")}</p>
          <p className="font-serif italic">{t("footer.made_in")}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
